// src/components/layout/ModernHeader.jsx

import React, { useState, useEffect, useRef } from 'react';
import { Feather, Key, Sun, Moon, Flame, BookOpen, Sparkles } from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────
function getTimeGreeting() {
  const h = new Date().getHours();
  if (h < 11) return 'Selamat Pagi';
  if (h < 15) return 'Selamat Siang';
  if (h < 18) return 'Selamat Sore';
  return 'Selamat Malam';
}

// ── Sub-components ────────────────────────────────────────

function AnimatedBlobs() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <div className="
        absolute -top-5 right-8 w-20 h-20 rounded-full
        bg-green-400 opacity-[0.12] blur-2xl
        animate-[drift_6s_ease-in-out_infinite_alternate]
      " />
      <div className="
        absolute top-2 right-24 w-14 h-14 rounded-full
        bg-sky-400 opacity-[0.12] blur-2xl
        animate-[drift_6s_ease-in-out_1.2s_infinite_alternate]
      " />
      <div className="
        absolute -top-2 right-40 w-12 h-12 rounded-full
        bg-lime-400 opacity-[0.10] blur-2xl
        animate-[drift_6s_ease-in-out_0.6s_infinite_alternate]
      " />
    </div>
  );
}

function LogoIcon() {
  return (
    <div className="
      w-10 h-10 rounded-2xl flex-shrink-0 relative overflow-hidden
      bg-gradient-to-br from-green-400 to-green-600
      flex items-center justify-center
      shadow-md shadow-green-500/20
      transition-transform duration-300
      hover:-rotate-6 hover:scale-110
    ">
      <Feather className="w-5 h-5 text-white relative z-10" strokeWidth={2.5} />
      {/* gloss overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
    </div>
  );
}

function MajedisBadge() {
  return (
    <span className="
      inline-flex items-center gap-1
      text-[9px] font-extrabold uppercase tracking-wide
      bg-green-100 text-green-700
      dark:bg-green-900/40 dark:text-green-400
      px-2 py-0.5 rounded-full mt-1
      relative overflow-hidden
    ">
      {/* shimmer sweep */}
      <span
        className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent"
        style={{ animation: 'badgeShine 3s ease-in-out infinite' }}
        aria-hidden="true"
      />
      <span className="relative z-10">Majelis Kalibata</span>
    </span>
  );
}

function LiveDot() {
  return (
    <span
      className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"
      style={{ animation: 'pulseDot 2s ease-in-out infinite' }}
      title="AI aktif"
      aria-label="AI aktif"
    />
  );
}

function StatPill({ icon: Icon, iconColor, value, label }) {
  return (
    <div className="
      flex items-center gap-1
      bg-white dark:bg-slate-800
      border border-stone-100 dark:border-slate-700
      rounded-full px-2 py-0.5
      text-[11px] text-slate-500 dark:text-slate-400
    ">
      <Icon className={`w-3 h-3 ${iconColor}`} />
      <strong className="text-slate-700 dark:text-slate-200 font-semibold">{value}</strong>
      {label}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────

export default function ModernHeader({
  onSettingsClick,
  userName = 'Sabenih',
  scrollContainerRef,    // ref ke elemen yang di-scroll (opsional)
  statsData = {          // data stats — override dari parent
    streakDays: 0,
    textsAnalyzed: 0,
    activeProvider: 'Gemini',
  },
}) {
  const [darkMode, setDarkMode]       = useState(false);
  const [collapsed, setCollapsed]     = useState(false);
  const [greeting, setGreeting]       = useState(getTimeGreeting());
  const headerRef                     = useRef(null);

  // ── Init dark mode dari localStorage
  useEffect(() => {
    const saved = localStorage.getItem('nahwu_dark_mode') === 'true';
    setDarkMode(saved);
    document.documentElement.classList.toggle('dark', saved);
  }, []);

  // ── Update salam setiap menit (kalau app dibuka lama)
  useEffect(() => {
    const id = setInterval(() => setGreeting(getTimeGreeting()), 60_000);
    return () => clearInterval(id);
  }, []);

  // ── Scroll-aware: pasang listener ke scrollContainerRef atau window
  useEffect(() => {
    const target = scrollContainerRef?.current ?? window;

    const onScroll = () => {
      const scrollY = scrollContainerRef?.current
        ? scrollContainerRef.current.scrollTop
        : window.scrollY;
      setCollapsed(scrollY > 40);
    };

    target.addEventListener('scroll', onScroll, { passive: true });
    return () => target.removeEventListener('scroll', onScroll);
  }, [scrollContainerRef]);

  // ── Toggle dark mode
  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('nahwu_dark_mode', String(next));
    document.documentElement.classList.toggle('dark', next);
  };

  return (
    <>
      {/* Keyframes — injected once */}
      <style>{`
        @keyframes drift {
          from { transform: translate(0,0) scale(1); }
          to   { transform: translate(6px,8px) scale(1.12); }
        }
        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.65); }
        }
        @keyframes badgeShine {
          0%       { transform: translateX(-100%); }
          40%, 100% { transform: translateX(220%); }
        }
        @keyframes waveHand {
          0%, 60%, 100% { transform: rotate(0deg); }
          15%  { transform: rotate(18deg); }
          30%  { transform: rotate(-8deg); }
          45%  { transform: rotate(14deg); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <header
        ref={headerRef}
        className="
          sticky top-0 z-40
          bg-white/96 dark:bg-slate-900/96
          backdrop-blur-xl
          transition-shadow duration-300
          overflow-hidden
        "
        style={{ boxShadow: collapsed ? '0 1px 0 0 var(--tw-border-opacity, rgba(0,0,0,0.08))' : 'none' }}
      >
        {/* Animated background blobs — hanya tampil saat tidak collapsed */}
        <div
          className="transition-opacity duration-300"
          style={{ opacity: collapsed ? 0 : 1 }}
        >
          <AnimatedBlobs />
        </div>

        {/* ── Baris atas: Logo + Tombol aksi ── */}
        <div className={`
          relative z-10 flex items-center justify-between gap-2
          px-4 transition-all duration-300
          ${collapsed ? 'py-2.5' : 'pt-3 pb-1'}
        `}>

          {/* Kiri: Logo */}
          <div className="flex items-center gap-2.5 min-w-0">
            <LogoIcon />

            <div className="min-w-0">
              <h1 className="
                text-base font-extrabold tracking-tight leading-none
                text-slate-800 dark:text-slate-100
                flex items-center gap-1.5
              ">
                Nahwu AI
                <LiveDot />
              </h1>
              <MajedisBadge />
            </div>
          </div>

          {/* Kanan: Tombol */}
          <div className="flex items-center gap-1.5 flex-shrink-0">

            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              title={darkMode ? 'Light Mode' : 'Dark Mode'}
              aria-label={darkMode ? 'Aktifkan light mode' : 'Aktifkan dark mode'}
              className="
                w-8 h-8 rounded-xl flex-shrink-0
                bg-stone-100 dark:bg-slate-800
                hover:bg-stone-200 dark:hover:bg-slate-700
                flex items-center justify-center
                transition-all duration-150 active:scale-90
              "
            >
              {darkMode
                ? <Sun  className="w-3.5 h-3.5 text-amber-400" />
                : <Moon className="w-3.5 h-3.5 text-slate-500" />
              }
            </button>

            {/* API Key button dengan fill-hover */}
            <button
              onClick={onSettingsClick}
              aria-label="Buka pengaturan API Key"
              className="
                group relative overflow-hidden
                flex items-center gap-1
                px-2.5 py-1.5 rounded-xl
                bg-green-50 dark:bg-green-900/30
                hover:bg-green-500
                border border-green-200 dark:border-green-800
                transition-all duration-200 active:scale-90
                flex-shrink-0
              "
            >
              <Key className="
                w-3 h-3
                text-green-700 dark:text-green-400
                group-hover:text-white
                transition-colors duration-200
              " />
              <span className="
                text-[10px] font-extrabold uppercase tracking-wide
                text-green-700 dark:text-green-400
                group-hover:text-white
                transition-colors duration-200
              ">
                API Key
              </span>
            </button>
          </div>
        </div>

        {/* ── Greeting section — collapse on scroll ── */}
        <div
          className="relative z-10 overflow-hidden transition-all duration-300 ease-in-out"
          style={{
            maxHeight: collapsed ? 0 : '120px',
            opacity:   collapsed ? 0 : 1,
            paddingTop:    collapsed ? 0 : undefined,
            paddingBottom: collapsed ? 0 : undefined,
          }}
          aria-hidden={collapsed}
        >
          <div className="px-4 pb-3 pt-1">

            {/* Salam + nama */}
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[17px] font-extrabold text-slate-800 dark:text-slate-100 leading-tight flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    style={{ display: 'inline-block', animation: 'waveHand 2.4s ease-in-out infinite', transformOrigin: '70% 80%' }}
                  >
                    👋
                  </span>
                  Halo, {userName}!
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                  <span className="font-semibold text-green-600 dark:text-green-400">{greeting}</span>
                  {' '}— yuk bedah teks Arab bersama AI
                </p>
              </div>
            </div>

            {/* Stats pills */}
            <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
              <div style={{ animation: 'fadeSlideUp 0.4s ease both 0s' }}>
                <StatPill
                  icon={Flame}
                  iconColor="text-orange-400"
                  value={statsData.streakDays}
                  label=" hari"
                />
              </div>
              <div style={{ animation: 'fadeSlideUp 0.4s ease both 0.07s' }}>
                <StatPill
                  icon={BookOpen}
                  iconColor="text-slate-400"
                  value={statsData.textsAnalyzed}
                  label=" teks"
                />
              </div>
              <div style={{ animation: 'fadeSlideUp 0.4s ease both 0.14s' }}>
                <StatPill
                  icon={Sparkles}
                  iconColor="text-purple-400"
                  value={statsData.activeProvider}
                  label=""
                />
              </div>
            </div>

          </div>
        </div>

        {/* Bottom border — muncul saat collapsed */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px bg-stone-100 dark:bg-slate-800 transition-opacity duration-300"
          style={{ opacity: collapsed ? 1 : 0 }}
          aria-hidden="true"
        />
      </header>
    </>
  );
}
